import { mediaUrl } from "@/lib/helpers";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp } from "lucide-react";
import { api } from "@/lib/api";
import { RankingTable, Sparkline } from "@/components/wei/Ranking";
import { SectionHeading, Skeleton } from "@/components/wei/Section";
import { VerifiedBadge, DemoTag } from "@/components/wei/Badges";

export function TopMovers({ limit = 5 }) {
  const [period, setPeriod] = useState("7d");
  const [items, setItems] = useState(null);
  useEffect(() => { setItems(null); api.get("/rankings/movers", { params: { period, limit } }).then((r) => setItems(r.data.items)).catch(() => setItems([])); }, [period, limit]);
  return (
    <div className="wei-card p-5" data-testid="top-movers">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-serif text-xl font-semibold inline-flex items-center gap-2"><TrendingUp className="w-5 h-5 text-gold" /> Top Movers</h3>
        <div className="flex border border-[#E6E3DC] rounded-sm overflow-hidden">{["24h", "7d", "30d"].map((p) => <button key={p} data-testid={`movers-tab-${p}`} onClick={() => setPeriod(p)} className={`px-3 py-1 text-[11px] font-semibold uppercase transition-colors ${period === p ? "bg-bordeaux text-white" : "text-charcoal hover:bg-stone"}`}>{p}</button>)}</div>
      </div>
      <ul className="divide-y divide-[#EFEDE8]">
        {(items || Array(limit).fill(null)).map((m, i) => m ? (
          <li key={m.slug} className="py-2.5 flex items-center gap-3" data-testid={`mover-${m.slug}`}>
            <img src={mediaUrl(m.image)} alt="" className="w-9 h-9 rounded-sm object-cover" />
            <div className="min-w-0 flex-1">
              <Link to={`/registry/${m.slug}`} className="text-sm font-medium hover:text-bordeaux inline-flex items-center gap-1 truncate">{m.name} <VerifiedBadge status={m.verification_status} compact /></Link>
              <div className="text-[11px] text-[#6B675F]">{m.category} · Score {m.score?.toFixed(1)}</div>
            </div>
            <div className="text-right">
              <div className="text-xs tabular-nums text-charcoal">#{m.previous_rank} → <strong className="text-ink">#{m.rank}</strong></div>
              <div className={`text-[11px] font-semibold ${m.movement > 0 ? "text-emerald-700" : m.movement < 0 ? "text-bordeaux" : "text-[#8A867E]"}`}>{m.movement > 0 ? "+" : ""}{m.movement} positions</div>
            </div>
          </li>
        ) : <li key={i} className="py-2.5"><Skeleton className="h-9" /></li>)}
      </ul>
    </div>
  );
}

export function RankingsHighlight() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/rankings/wei-global-100", { params: { period: "7d" } }).then((r) => setData(r.data)).catch(() => {}); }, []);
  return (
    <section data-testid="rankings-highlight" className="container-wei py-10">
      <SectionHeading eyebrow="WEI Classifications" title="Rankings Highlights" to="/rankings" linkLabel="All Rankings" id="rankings" />
      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="wei-card p-5">
          <div className="flex items-center justify-between mb-3">
            <div><h3 className="font-serif text-xl font-semibold">WEI Global 100</h3><p className="text-xs text-[#6B675F]">Evidence-based classification · Movement vs. 7 days · As of {data?.as_of}</p></div>
            <div className="flex items-center gap-3"><DemoTag /><Sparkline data={[-6, -6, -5, -5, -4, -4, -3, -3, -2, -1, -1, -1]} color="#8B001B" /></div>
          </div>
          {data ? <RankingTable items={data.items.slice(0, 6)} period="7d" compact /> : <Skeleton className="h-64" />}
          <Link to="/rankings/wei-global-100" data-testid="view-full-index-btn" className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-bordeaux hover:underline">View full index <ArrowRight className="w-4 h-4" /></Link>
        </div>
        <TopMovers />
      </div>
    </section>
  );
}
